import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import RplTable from '../../../components/tables/RplTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useRplApi } from '../../../hooks/useRplApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmAction, confirmDelete } from '../../../utils/sweetAlertUtils';
import {
  AuthDTO,
  EmailMessageDTO,
  ImpersonationDTO,
  RplDTO,
  NoteDTO,
} from '../../../services/apiClient';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRoles, getUserId } from '../../../utils/tokenUtils';
import { useCourseTypes } from '../../../hooks/useCourseTypeApi';
import { useRplStatuses } from '../../../hooks/useRplStatusApi';
import Drawer from '../../../components/common/Drawer';
import { useFormValidation, ValidationRules } from '../../../hooks/useFormValidation';
import { useMemberCategories } from '../../../hooks/useMemberCategoryApi';
import { RplStatusConstants } from '../../../constants/RplStatusConstants';
import { MemberStatusConstants } from '../../../constants/MemberStatusConstants';

import { useAuthApi } from '../../../hooks/useAuthApi';
import RplQuestions from '../../../components/drawer/RplQuestions';
import FeedbackForm from '../../../components/forms/FeedbackForm';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { DrawerViewConstants, RplDrawerViews } from '../../../constants/DrawerViewConstants';
import { useEmailMessageApi } from '../../../hooks/useEmailMessageApi';
import EmailDrawerContent from '../../../components/common/EmailDrawerContent';
import { useEmailHandlers } from '../../../hooks/useEmailHandlers';
import { useNoteApi } from '../../../hooks/useNoteApi';
import AddNote from '../../../components/drawer/AddNote';
import ViewNoteHistory from '../../../components/drawer/ViewNoteHistory';
import MemberQuickDetails from '../../../components/drawer/MemberQuickDetails';
import { useImpersonation } from '../../../hooks/useImpersonation';
import ViewInvoiceHistory from '../../../components/drawer/ViewInvoiceHistory';
import { usePaymentApi } from '../../../hooks/usePaymentApi';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { ChevronDown } from 'lucide-react';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';
import { FileViewer } from '../../../components/common/FileViewer';
import { useDedicatedStatusApi } from '../../../hooks/useDedicatedStatusApi';

const defaultSelectedItem: RplDTO = {};

const RplList: React.FC = () => {
  const { search, remove, extract, restoreFromDeleted, sendProofOfPaymentLink, isLoading, edit, updateStatus, getForfeitureDocument } = useRplApi();
  const { generateCertificate: generateDedicatedCertificate } = useDedicatedStatusApi();
  const { processEftPayment } = usePaymentApi();
  const [items, setItems] = useState<RplDTO[]>([]);

  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    showAdvanced,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    setShowAdvanced,
    commitSearch,
    clearSearchState,
  } = useSearchState('rpls', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedItem, setSelectedItem] = useState<RplDTO>(defaultSelectedItem);
  const [defaultStatusId, setDefaultStatusId] = useState<string>();
  const [drawerView, setDrawerView] = useState<RplDrawerViews>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [drawerTitle, setDrawerTitle] = useState<string>('');
  const [selectedRplIds, setSelectedRplIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [drawerSize, setDrawerSize] = useState<'small' | 'large'>('large');
  const bulkActionsRef = useRef<HTMLDivElement>(null);
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string } | null>(null);

  const statusFieldName = 'rplStatusId';

  const [courseTypes, setCourseTypes] = useState<{ value: string; label: string }[]>([]);
  const [rplStatuses, setRplStatuses] = useState<{ value: string; label: string }[]>([]);
  const [memberCategories, setMemberCategories] = useState<{ value: string; label: string }[]>([]);

  const roles = getRoles();
  const navigate = useNavigate();
  const location = useLocation();
  const { sendEmail, isLoading: isSendingEmail } = useEmailMessageApi();
  const { create: createNote, isLoading: isSubmittingNote } = useNoteApi();

  const statusFromUrl = location.pathname.includes('/status/')
    ? location.pathname.split('/status/')[1].toLowerCase()
    : '';

  // Clear items when route variant changes (prevents showing stale data from previous page)
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname) {
      setItems([]); // Clear old data immediately
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  const [statusMap, setStatusMap] = useState<{ [key: string]: string }>({});

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Rpls', href: '#' },
  ];

  const approveItemValidationRules: ValidationRules = {
    rplStatusId: [{ required: true, message: 'Status is required' }],
    'member.memberCategoryId': [{ required: true, message: 'FCA Status is required' }],
    'member.memberTypeId': [{ required: true, message: 'Membership is required' }],
  };

  const { errors: approveItemErrors, validate: approveItemValidate } = useFormValidation<RplDTO>(
    approveItemValidationRules,
  );
  const getFiltersFromStatus = (status: string) => {

    switch (status.toLowerCase()) {
      case 'sport':
        return {
          courseTypeId: statusMap['sport'],
          isDeleted: false,
        };
      case 'hunt':
        return {
          courseTypeId: statusMap['hunt'],
          isDeleted: false,
        };
      case 'huntsport':
        return {
          courseTypeId: statusMap['huntsport'],
          isDeleted: false,
        };
      case 'deleted':
        return { isDeleted: true };
      default:
        return { isDeleted: false };
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'sport':
        return 'Sport';
      case 'hunt':
        return 'Hunt';
      case 'huntsport':
        return 'Hunt & Sport';
      case 'deleted':
        return 'Deleted';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const { data: courseTypesData = [], isLoading: isLoadingCourseTypes } = useCourseTypes();
  const { data: rplStatusesData = [], isLoading: isLoadingRplStatuses } = useRplStatuses();
  const { data: memberCategoriesData, isLoading: isLoadingMemberCategories } = useMemberCategories();

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const statusMapping: { [key: string]: string } = {};
        courseTypesData?.forEach((course) => {
          if (course.title?.toLowerCase() === 'dedicated sports-person') {
            statusMapping['sport'] = course.id ?? '';
          }
          if (course.title?.toLowerCase() === 'dedicated hunter') {
            statusMapping['hunt'] = course.id ?? '';
          }
          if (course.title?.toLowerCase() === 'dedicated hunter & sports-person') {
            statusMapping['huntsport'] = course.id ?? '';
          }
        });
        setStatusMap(statusMapping);

        // Set initial filters based on URL status
        if (statusFromUrl) {
          const statusFilters = getFiltersFromStatus(statusFromUrl);
          setSearchFilters((prev: any) => ({
            ...prev,
            ...statusFilters,
          }));
        }

        setCourseTypes(
          courseTypesData?.map((course) => ({
            value: course.id ?? '',
            label: course.title ?? '',
          })) || [],
        );
        setRplStatuses(
          rplStatusesData?.map((status) => ({
            value: status.id ?? '',
            label: status.title ?? '',
          })) || [],
        );
        setMemberCategories(
          memberCategoriesData?.map((category) => ({
            value: category.id ?? '',
            label: category.title ?? '',
          })) || [],
        );
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };

    loadDropdownData();
  }, [statusFromUrl, memberCategoriesData, courseTypesData, rplStatusesData]);

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const statusFilters = getFiltersFromStatus(statusFromUrl);

      const response = await search({
        filters: {
          ...filters,
          ...statusFilters,
        },
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Rpls:', error);
      return 0;
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const previousFiltersRef = useRef<string>('');
  const isCommittingSearchRef = useRef<boolean>(false);

  // Sync check: get current URL filters on every render to detect mismatches
  const getCurrentUrlFilters = () => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });
    return JSON.stringify(urlFilters);
  };

  const currentUrlFiltersKey = getCurrentUrlFilters();
  const areFiltersStale = items.length > 0 && previousFiltersRef.current !== currentUrlFiltersKey;

  // Fetch items when dependencies change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};

    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });

    const currentFiltersKey = JSON.stringify(urlFilters);
    
    // Skip this effect if we're in the middle of a commitSearch AND the URL hasn't actually changed yet
    if (isCommittingSearchRef.current && currentFiltersKey === previousFiltersRef.current) {
      return;
    }
    
    // URL has now updated, clear the flag
    if (isCommittingSearchRef.current) {
      isCommittingSearchRef.current = false;
    }

    // Parse page and pageSize from URL (source of truth) to avoid stale state
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const page = urlPage ? parseInt(urlPage, 10) : 1;
    const size = urlPageSize ? parseInt(urlPageSize, 10) : pageSize;

    // If URL has no filter params but searchFilters state has some, clear the state
    // This handles the case where user navigates between routes and searchFilters is stale
    const hasUrlFilters = Object.keys(urlFilters).length > 0;
    const hasStateFilters = searchFilters && Object.keys(searchFilters).length > 0;
    if (!hasUrlFilters && hasStateFilters) {
      setSearchFilters({});
    }

    // Clear items immediately if filters changed (not just page number)
    if (previousFiltersRef.current !== currentFiltersKey) {
      setItems([]);
      previousFiltersRef.current = currentFiltersKey;
    }

    fetchItems(page, size, {
      ...urlFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, statusFromUrl]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, {
      ...searchFilters,
    });
  };

  const handleMemberClick = (memberId: string) => {
    // Find the RPL with this memberId to get the member's ID/Passport number
    const rpl = items.find(item => item.memberId === memberId);
    const idPassportNumber = rpl?.member?.idPassportNumber || '';

    // Set flag to prevent intermediate useEffect from running with stale URL
    isCommittingSearchRef.current = true;

    // Clear all filters and search only by ID/Passport number.
    const newFilters = idPassportNumber ? { idPassportNumber } : {};
    // The useEffect watching location.search will trigger the fetch automatically
    commitSearch(newFilters, { page: 1, showAdvanced: false, replace: false });
  };

  const handleClearSearch = () => {
    clearSearchState();
    fetchItems(1, pageSize, {});
  };

  const handleItemApproveSubmit = async (rpl: RplDTO) => {
    if (selectedItem && approveItemValidate(selectedItem)) {
      try {
        await edit(rpl.id ?? '', rpl);
        fetchItems(currentPage, pageSize, {
          ...searchFilters,
          ...getFiltersFromStatus(statusFromUrl),
        });
      } catch (error) {
        console.error('Error updating Rpl:', error);
      }
      handleDrawerClose();
    }
  };

  const handleDelete = async (dto: RplDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting Rpl:', error);
    }
  };

  // Email handlers from useEmailHandlers hook
  const { handleSendEmail, handleSendEmailSubmit, handleViewAllEmails, handleViewRecordEmails } =
    useEmailHandlers<RplDTO>({
      setSelectedItem,
      setDrawerTitle,
      setDrawerView,
      setIsDrawerOpen,
      sendEmail,
      fetchItems: async (page: number, size: number, filters: any) => {
        await fetchItems(page, size, filters);
      },
      currentPage,
      pageSize,
      searchFilters,
    });

  // Add export handler
  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Rpls:', error);
    }
  };

  const handleRestore = async (rpl: RplDTO) => {
    try {
      await restoreFromDeleted(rpl.id ?? '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error restoring rpl:', error);
    }
  };

  const handleOpenDrawer = async (
    rpl: RplDTO,
    drawerView: RplDrawerViews,
    drawerTitle: string,
    drawerSize: 'small' | 'large' = 'large',
    defaultStatusId?: string,
  ) => {
    setSelectedItem(rpl);
    setDrawerView(drawerView);
    setDrawerTitle(drawerTitle);
    setDrawerSize(drawerSize);
    if (defaultStatusId) setDefaultStatusId(defaultStatusId);
    setIsDrawerOpen(true);
  };

  const handleAddNote = async (data: NoteDTO) => {
    try {
      setIsSubmitting(true);
      await createNote(data);
      setIsDrawerOpen(false);
      setSelectedItem({});
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error creating note: ', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDrawerClose = () => {
    setSelectedItem({});
    setDefaultStatusId(undefined);
    setIsDrawerOpen(false);
    setDrawerSize('large');
  };

  const { impersonate } = useImpersonation();

  const handleStatusSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      // Determine which RPL IDs to update
      const idsToUpdate = selectedRplIds.size > 0
        ? Array.from(selectedRplIds)
        : [selectedItem.id!];

      // Prepare bulk update DTO
      const updateDto = {
        recordIds: idsToUpdate,
        rplStatusId: data.rplStatusId,
        feedback: data.feedback,
        replyTemplateId: data.replyTemplateId,
        sendFeedbackEmail: data.sendFeedbackEmail || false,
        member: data.member,
      };

      // Call the bulk update endpoint
      await updateStatus(updateDto);

      setIsDrawerOpen(false);
      setSelectedItem({});
      setSelectedRplIds(new Set()); // Clear selections
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendProofOfPaymentLink = async (rpl: RplDTO) => {
    try {
      await sendProofOfPaymentLink(rpl.id ?? '');
    } catch (error) {
      console.error('Error sending proof of payment link:', error);
    }
  };

  const handleCompleteEftPayment = async (rpl: RplDTO) => {
    try {
      await processEftPayment({ componentId: rpl.id, entityType: 'Rpl' });
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error completing EFT payment:', error);
    }
  };

  const handleDownloadOriginalCertificate = async (rpl: RplDTO) => {
    try {
      const url = await generateDedicatedCertificate(rpl.courseTypeId ?? '', rpl.id ?? '');
      setViewingFile({ name: `Dedicated-Status-Certificate-${rpl.id}.pdf`, url });
    } catch (error) {
      console.error('Error generating original certificate:', error);
    }
  };

  const handleDownloadForfeitureDocument = async (rpl: RplDTO) => {
    try {
      const url = await getForfeitureDocument(rpl.id ?? '');
      setViewingFile({ name: `Forfeiture-Document-${rpl.id}.pdf`, url });
    } catch (error) {
      console.error('Error generating forfeiture document:', error);
    }
  };

  const handleSelectRow = (rplId: string) => {
    setSelectedRplIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rplId)) {
        newSet.delete(rplId);
      } else {
        newSet.add(rplId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allIds = items.filter((item) => !item.isDeleted).map((item) => item.id!);
      setSelectedRplIds(new Set(allIds));
    } else {
      setSelectedRplIds(new Set());
    }
  };

  const handleBulkStatusUpdate = () => {
    const firstSelectedId = Array.from(selectedRplIds)[0];
    const firstRpl = items.find(r => r.id === firstSelectedId) || {};
    const count = selectedRplIds.size;
    handleOpenDrawer(
      firstRpl as RplDTO,
      DrawerViewConstants.FEEDBACK,
      `Update Status (${count} RPL${count > 1 ? 's' : ''})`
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target as Node)) {
        setShowBulkActions(false);
      }
    };

    if (showBulkActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBulkActions]);

  const handleView = (rpl: RplDTO) => {
    handleOpenDrawer(rpl, 'view', 'View RPL');
  };

  return (
    <AdminLayout
      activeMenuItem={statusFromUrl ? `/admin/rpls/status/${statusFromUrl}` : '/admin/rpls'}
      breadcrumbs={[
        { label: 'Admin', href: '#' },
        { label: 'Rpls', href: '#' },
        ...(statusFromUrl
          ? [
            {
              label: getStatusDisplayName(statusFromUrl),
              href: '#',
            },
          ]
          : []),
      ]}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Manage.Rpl.View"
    >
      <Card
        title={`Manage Rpls ${statusFromUrl ? `- ${getStatusDisplayName(statusFromUrl)}` : ''}`}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            {roles?.includes('Admin') && selectedRplIds.size > 0 && (
              <div ref={bulkActionsRef} className="relative">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="flex items-center gap-2"
                >
                  Bulk Actions
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full">
                    {selectedRplIds.size}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${showBulkActions ? 'rotate-180' : ''
                      }`}
                  />
                </Button>
                {showBulkActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => {
                        handleBulkStatusUpdate();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg"
                    >
                      Update Status
                    </button>
                  </div>
                )}
              </div>
            )}
            {roles?.includes('Admin') && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowAdvanced(!showAdvanced);
                  }}
                >
                  {showAdvanced ? 'Basic Search' : 'Advanced Search'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleExport}>
                  Export
                </Button>
              </>
            )}

            {roles?.includes('User') && (
              <AddNewButton onClick={() => navigate('/admin/rpls/create')} />
            )}
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="firstName"
                label="Search by First Name"
                value={searchFilters.firstName || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firstName: e.target.value,
                  })
                }
                placeholder="Search by First Name"
              />
              <FormField
                id="lastName"
                label="Search by Surname"
                value={searchFilters.lastName || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, lastName: e.target.value })}
                placeholder="Search by Surname"
              />
              <FormField
                id="idPassportNumber"
                label="Search by ID Number"
                value={searchFilters.idPassportNumber || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, idPassportNumber: e.target.value })}
                placeholder="Search by ID Number"
              />
              <FormField
                id="courseTypeId"
                label="Search by Course Type"
                value={searchFilters.courseTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    courseTypeId: e.target.value,
                  })
                }
                placeholder="Search by Course Type Id"
                options={courseTypes}
                isLoading={isLoadingCourseTypes}
              />
              <FormField
                id="rplStatusId"
                label="Search by Rpl Status"
                value={searchFilters.rplStatusId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    rplStatusId: e.target.value,
                  })
                }
                placeholder="Search by Rpl Status"
                options={rplStatuses}
                isLoading={isLoadingRplStatuses}
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex space-x-4 mb-4">
            {showAdvanced && (
              <>
                <Button variant="secondary" onClick={handleClearSearch}>
                  Clear Search
                </Button>
                <Button type="submit">Search</Button>
              </>
            )}
            {!showAdvanced && roles?.includes('Admin') && (
              <GeneralSearch
                searchFilters={searchFilters}
                setSearchFilters={setSearchFilters}
              />
            )}
          </div>
        </form>

        <RplTable
          data={areFiltersStale ? [] : items}
          handleViewMemberDetails={(dto) => handleOpenDrawer(dto, DrawerViewConstants.VIEW_MEMBER_DETAILS, 'Member Details', 'small')}
          handleView={handleView}
          handleEdit={(dto) => navigate(`/admin/rpls/${dto.id}`)}
          handleDelete={handleDelete}
          handleSendEmail={handleSendEmail}
          handleViewAllEmails={handleViewAllEmails}
          handleViewRecordEmails={handleViewRecordEmails}
          handleRestore={handleRestore}
          handleApprove={(dto) =>
            handleOpenDrawer(
              dto,
              DrawerViewConstants.FEEDBACK,
              'Approve',
              'large',
              RplStatusConstants.APPROVED,
            )
          }
          handleChangeStatus={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.FEEDBACK, 'Change Status')
          }
          handleImpersonateMemberLogin={(dto) => impersonate(dto.memberId, dto.member?.memberStatusId)}
          handleAddNote={(dto) => handleOpenDrawer(dto, DrawerViewConstants.ADD_NOTE, 'Add Note')}
          handleViewNoteHistory={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.VIEW_NOTE_HISTORY, 'Notes History')
          }
          handleViewInvoiceHistory={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.VIEW_INVOICE_HISTORY, 'Invoice History')
          }
          handleSendProofOfPaymentLink={handleSendProofOfPaymentLink}
          handleCompleteEftPayment={handleCompleteEftPayment}
          handleDownloadForfeitureDocument={handleDownloadForfeitureDocument}
          handleDownloadOriginalCertificate={handleDownloadOriginalCertificate}
          handleMemberClick={handleMemberClick}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
          {...(roles?.includes('Admin')
            ? {
              selectedIds: selectedRplIds,
              onSelectRow: handleSelectRow,
              onSelectAll: handleSelectAll,
            }
            : {})}
        />

        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          title={drawerTitle}
          size={drawerSize}
        >
          {drawerView === DrawerViewConstants.VIEW_MEMBER_DETAILS && (
            <MemberQuickDetails
              memberId={selectedItem.member?.id ?? ''}
              onClose={handleDrawerClose}
            />
          )}
          {drawerView === 'view' && <RplQuestions rpl={selectedItem} onClose={handleDrawerClose} />}
          {drawerView === DrawerViewConstants.FEEDBACK && (
            <FeedbackForm
              componentId={ComponentConstants.RPL}
              recordId={selectedItem.id ?? ''}
              componentStatuses={rplStatuses}
              statusFieldName={statusFieldName}
              defaultStatusId={defaultStatusId}
              initialData={selectedItem}
              onSubmit={handleStatusSubmit}
              onCancel={handleDrawerClose}
              updateLoading={isSubmitting}
            />
          )}
          {drawerView === DrawerViewConstants.ADD_NOTE && (
            <AddNote
              memberId={selectedItem.member?.id || ''}
              componentId={ComponentConstants.RPL}
              recordId={selectedItem.id ?? ''}
              onSubmit={handleAddNote}
              onCancel={handleDrawerClose}
              isLoading={isSubmitting}
            />
          )}
          {drawerView === DrawerViewConstants.VIEW_NOTE_HISTORY && (
            <ViewNoteHistory memberId={selectedItem.member?.id || ''} />
          )}
          {drawerView === DrawerViewConstants.VIEW_INVOICE_HISTORY && (
            <ViewInvoiceHistory memberId={selectedItem.memberId ?? ''} />
          )}
          <EmailDrawerContent
            drawerView={drawerView}
            selectedItem={selectedItem}
            componentId={ComponentConstants.RPL}
            getMemberId={(dto) => dto?.member?.id || ''}
            onEmailSubmit={handleSendEmailSubmit}
            onClose={handleDrawerClose}
            isSending={isSendingEmail}
          />
        </Drawer>
      </Card>

      {viewingFile && (
        <FileViewer
          sasUrl={viewingFile.url}
          fileName={viewingFile.name}
          onClose={() => setViewingFile(null)}
        />
      )}
    </AdminLayout>
  );
};

export default RplList;
